import React, { useCallback, useEffect, useState } from 'react';
import { usePartsLookupOrgData } from '../../hooks/usePartsLookupOrgData';
import { useOrgTheming } from '../../hooks/useOrgTheming';
import { useFormVerification } from '../../hooks/useFormVerification';
import VerifyStep from './VerifyStep';
import ShopStep from './ShopStep';
import ReviewStep from './ReviewStep';
import type { ShopMachine } from './MachinePartsSections';
import type { MaintenancePart } from '../../hooks/useMachineHub';
import type { HubPartIdentification } from '../../services/hubApi';
import {
  MAX_ITEM_QUANTITY,
  PartsOrderCartItem,
  WidgetAccount,
  probe,
} from '../../services/partsOrderApi';
import '../Agent/AgentChat.scss';
import './PartsOrder.scss';

/**
 * Verified parts-ordering flow, shared by the standalone /partsOrder page and
 * the <opervia-parts-order> widget: probe (PFW configured?) -> verify contact
 * -> shop (search + cart) -> review (location + submit) -> confirmation.
 * A verified-but-unmatched contact still shops; the server files the submit
 * as a parts lead instead of an order.
 */

export type OrderConfirmation =
  | {
      type: 'order';
      orderNumber: string | null;
      estimatedReadyAt?: string | null;
      locationName?: string | null;
    }
  | { type: 'lead' };

type Step = 'unavailable' | 'verify' | 'shop' | 'review' | 'confirm';

interface PartsOrderAppProps {
  organizationId: string;
  embedded?: boolean;
  /** Skip the org/"Order Parts" masthead (the machine hub shell renders its own). */
  bare?: boolean;
  // Machine-hub mode: the hub already verified the customer and owns the session,
  // so the app starts at the shop with the hub's code and its session secret and defers expiry upstream.
  initialCodeId?: number | null;
  initialSession?: string | null;
  initialPhase?: 'verified' | 'no_match';
  customerFirstName?: string;
  machine?: ShopMachine;
  maintenanceParts?: MaintenancePart[];
  onVerificationExpired?: () => void;
  /** Hub only: "Describe it" search through the hub session (see ShopStep). */
  onDescribe?: (description: string) => Promise<HubPartIdentification>;
  onQuoteRequested?: (draft?: string) => void;
}

function formatReadyDate(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

function PartsOrderApp({
  organizationId, embedded = false, bare = false, initialCodeId, initialSession, initialPhase, customerFirstName,
  machine, maintenanceParts, onVerificationExpired, onDescribe, onQuoteRequested,
}: PartsOrderAppProps) {
  const { data: orgData, loading: orgLoading, error: orgError } = usePartsLookupOrgData(organizationId);
  const verification = useFormVerification(organizationId);
  const [externalCode] = useState(initialCodeId ?? null);
  const [externalSession] = useState(initialSession ?? null);
  const codeId = externalCode ?? verification.codeId;

  const [probed, setProbed] = useState(false);
  const [step, setStep] = useState<Step>(externalCode != null ? 'shop' : 'verify');
  // Latest accounts a search reported; later searches that omit the field keep it.
  const [accounts, setAccounts] = useState<WidgetAccount[]>([]);
  const [cart, setCart] = useState<PartsOrderCartItem[]>([]);
  const [confirmation, setConfirmation] = useState<OrderConfirmation | null>(null);
  const [verifyNotice, setVerifyNotice] = useState<string | null>(null);
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  useOrgTheming(
    orgData?.organizationSettings?.primary_color,
    document.querySelector('opervia-parts-order') as HTMLElement | null,
  );

  useEffect(() => {
    let cancelled = false;
    probe(organizationId)
      .then((res) => {
        if (cancelled) return;
        if (!res.available) setStep('unavailable');
        setProbed(true);
      })
      .catch(() => {
        // Fail open: a rate-limited or flaky probe must not mask a configured
        // org — a truly missing config still surfaces as pfw_not_configured
        // at first search.
        if (cancelled) return;
        setProbed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Both verified (matched customer) and no_match (verified contact, lead
  // mode) unlock the shop — the server decides order vs lead at submit.
  useEffect(() => {
    if (step === 'verify' && (verification.phase === 'verified' || verification.phase === 'no_match')) {
      setStep('shop');
    }
  }, [step, verification.phase]);

  // The expiry notice belongs to the idle verify form; drop it once the user
  // acts (requests a new code) so it doesn't linger over a fresh attempt.
  useEffect(() => {
    if (verifyNotice && verification.phase !== 'idle') setVerifyNotice(null);
  }, [verifyNotice, verification.phase]);

  const addToCart = useCallback((item: PartsOrderCartItem) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.part_number === item.part_number);
      if (existing) {
        // Re-adding an already-carted part increments its quantity.
        return prev.map((i) =>
          i.part_number === item.part_number
            ? { ...i, quantity: Math.min(MAX_ITEM_QUANTITY, i.quantity + item.quantity) }
            : i,
        );
      }
      return [...prev, item];
    });
  }, []);

  const updateQuantity = useCallback((partNumber: string, quantity: number) => {
    const clamped = Math.max(1, Math.min(MAX_ITEM_QUANTITY, quantity));
    setCart((prev) =>
      prev.map((i) => (i.part_number === partNumber ? { ...i, quantity: clamped } : i)),
    );
  }, []);

  const removeItem = useCallback((partNumber: string) => {
    setCart((prev) => prev.filter((i) => i.part_number !== partNumber));
  }, []);

  // Server 401s a code consumed more than 60 minutes ago — restart verification.
  const handleVerificationExpired = useCallback(() => {
    if (externalCode != null) { onVerificationExpired?.(); return; }
    verification.reset();
    setVerifyNotice('Your verification expired — please verify again to keep ordering.');
    setStep('verify');
  }, [externalCode, onVerificationExpired, verification]);

  const handleSubmitted = useCallback((result: OrderConfirmation) => {
    setConfirmation(result);
    setStep('confirm');
  }, []);

  const wrapperClass = `public-form-page parts-order-page${embedded ? ' opervia-widget' : ''}`;

  if (orgError || (!orgLoading && !orgData?.organization)) {
    return (
      <div className={wrapperClass}>
        <div className="error-message">{orgError || 'Organization not found.'}</div>
      </div>
    );
  }

  if (orgLoading || !probed || !orgData) {
    return (
      <div className={wrapperClass}>
        <div className="loading-indicator">Loading...</div>
      </div>
    );
  }

  const orgName = orgData.organization.name;
  const orgPhone = orgData.organizationPhoneNumber?.phone_number;
  const leadMode = initialPhase ? initialPhase === 'no_match' : verification.phase === 'no_match';

  // The unverified other contact field — lead enrichment only, never matching.
  const enrichment =
    verification.channel === 'sms'
      ? contactEmail.trim()
        ? { email: contactEmail.trim() }
        : {}
      : contactPhone.trim()
        ? { phone: contactPhone.trim() }
        : {};

  let body: React.ReactNode;
  if (step === 'unavailable') {
    body = (
      <div className="parts-lookup-result result-info">
        <div className="result-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
        </div>
        <h3>Online Ordering Not Available</h3>
        <p>
          {orgName} doesn't offer online parts ordering yet.
          {orgPhone ? (
            <> Call us at <a href={`tel:${orgPhone}`} className="phone-link">{orgPhone}</a> and our parts team will be glad to help.</>
          ) : (
            <> Please contact our parts team and we'll be glad to help.</>
          )}
        </p>
      </div>
    );
  } else if (step === 'verify') {
    body = (
      <VerifyStep
        verification={verification}
        phone={contactPhone}
        email={contactEmail}
        onPhoneChange={setContactPhone}
        onEmailChange={setContactEmail}
        notice={verifyNotice}
      />
    );
  } else if (step === 'shop') {
    body = (
      <ShopStep
        organizationId={organizationId}
        codeId={codeId}
        session={externalSession}
        customerFirstName={customerFirstName ?? verification.customer?.firstName}
        cart={cart}
        onAdd={addToCart}
        onUpdateQuantity={updateQuantity}
        onRemove={removeItem}
        onReview={() => setStep('review')}
        onVerificationExpired={handleVerificationExpired}
        onAccounts={setAccounts}
        machine={machine}
        maintenanceParts={maintenanceParts}
        onDescribe={onDescribe}
        onQuoteRequested={onQuoteRequested}
      />
    );
  } else if (step === 'review') {
    body = (
      <ReviewStep
        organizationId={organizationId}
        codeId={codeId}
        session={externalSession}
        leadMode={leadMode}
        enrichment={enrichment}
        accounts={accounts}
        cart={cart}
        onUpdateQuantity={updateQuantity}
        onRemove={removeItem}
        onBack={() => setStep('shop')}
        onSubmitted={handleSubmitted}
        onVerificationExpired={handleVerificationExpired}
        onAlreadySubmitted={externalCode != null ? handleVerificationExpired : undefined}
      />
    );
  } else if (confirmation?.type === 'lead') {
    body = (
      <div className="parts-lookup-result result-success">
        <div className="result-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
        </div>
        <h3>Request Received!</h3>
        <p>
          We don't have an account on file for your contact info yet, so our parts team
          will reach out to confirm availability and pricing before anything is ordered.
        </p>
      </div>
    );
  } else {
    const readyCopy =
      confirmation?.type === 'order' && confirmation.estimatedReadyAt
        ? formatReadyDate(confirmation.estimatedReadyAt)
        : null;
    body = (
      <div className="parts-lookup-result result-success">
        <div className="result-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
        </div>
        <h3>Order Placed!</h3>
        {confirmation?.type === 'order' && confirmation.orderNumber && (
          <p>
            Your order number is <strong>{confirmation.orderNumber}</strong>.
          </p>
        )}
        <p>
          Your parts order has been sent to {orgName}.
          {confirmation?.type === 'order' && confirmation.locationName && (
            <> Pick it up at <strong>{confirmation.locationName}</strong>.</>
          )}
          {readyCopy && <> Estimated ready by {readyCopy}.</>}
        </p>
        <p>Our parts team will reach out if anything needs attention.</p>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="public-form-app-container">
        {!bare && (
          <header className="form-header">
            <div className="form-header-content">
              <div className="header-text">
                <h1>{orgName}</h1>
                <h2>Order Parts</h2>
              </div>
            </div>
          </header>
        )}

        <div className="parts-lookup-card">{body}</div>
      </div>
    </div>
  );
}

export default PartsOrderApp;
